import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, of, throwError } from 'rxjs';
import { environment } from '@env/environment';
import {
  ProfileFormDataModel,
  ProfileStyleModel
} from '@models/profile-form-data.model';
import { PersonModel } from '@models/person.model';
import {
  ICheckBlacklistedWordReqBody,
  ICreateDocumentReqBody,
  ICreateDocumentResBody,
  ICreateProfileReqBody,
  ICreateSingleDocumentReqBody,
  IEventReqBody,
  IProfileToJson,
  ProfileEventModel,
  ProfileModel,
  ProfilePhotoEnum,
  ProfilePhotoModel,
  ProfileRevisionStatusEnum
} from '@models/profile.model';
import { NetVerifiedCountriesModel } from '@models/net-verified-countries.model';
import { CopyProfileOptions } from '@app/pages/my/features/services/data-access/models/all-services.model';
import {
  DocumentModel,
  DocumentStatusEnum,
  DocumentTypeEnum,
  ManualUploadedDocumentEnum
} from '@models/document.model';
import { catchError } from 'rxjs/operators';
import { IdenfyStatusEnum } from '@models/idenfy.model';
import {
  IDocumentNotificationResponse,
  INotificationCountResponse,
  NotificationModel,
  NotificationType
} from '@models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class EscortProfileService {
  #httpClient = inject(HttpClient);
  #baseUrl = `${environment.apiUrl}/escort-profile`;
  netVerifiedCountries$: Observable<NetVerifiedCountriesModel[]>;

  getProfileFormData(): Observable<ProfileFormDataModel> {
    const url = `${this.#baseUrl}/form-data`;
    const params = new HttpParams().set('v', 'snake');

    return this.#httpClient
      .get<ProfileFormDataModel>(url, { params })
      .pipe(map((res) => res))
      .pipe(map((json: any) => ProfileFormDataModel.fromJson(json)));
  }

  getPersons(accountId: number): Observable<PersonModel[]> {
    const url = `${this.#baseUrl}/persons`;
    const params = new HttpParams().set('account_id', accountId.toString());

    return this.#httpClient
      .get(url, { params })
      .pipe(
        map((json: any[]) =>
          json
            .map((item) => PersonModel.fromJson(item))
            .filter((person) => !person.isDeactivated)
        )
      );
  }

  getProfileById(id: string | number): Observable<ProfileModel> {
    const url = `${this.#baseUrl}/${id}`;

    return this.#httpClient
      .get(url)
      .pipe(map((json) => ProfileModel.fromJson(json)));
  }

  checkBlacklistedWord(
    reqBody: ICheckBlacklistedWordReqBody
  ): Observable<void> {
    const url = `${this.#baseUrl}/rev/validate`;

    return this.#httpClient.post<void>(url, reqBody);
  }

  getAllServiceAds(
    accountId: number,
    personId: string = null,
    isSecret: 0 | 1 = 0
  ): Observable<ProfileModel[]> {
    let params = new HttpParams().set('account_id', accountId.toString());
    const url = `${this.#baseUrl}`;

    if (personId) {
      params = params.set('person_id', personId);
    }

    if (isSecret) {
      params = params.set('is_secret', isSecret);
    }

    return this.#httpClient
      .get<ProfileModel[]>(url, { params })
      .pipe(map((json) => json.map((item) => ProfileModel.fromJson(item))));
  }

  getLastRev(profileId: any): Observable<ProfileModel> {
    const url = `${this.#baseUrl}/rev/latest/?profile_id=${profileId}`;
    return this.#httpClient
      .get(url)
      .pipe(map((json: any) => ProfileModel.fromJson(json)));
  }

  getNetVerifyCountries(): Observable<NetVerifiedCountriesModel[]> {
    if (this.netVerifiedCountries$) {
      return this.netVerifiedCountries$;
    }

    const url = `${environment.apiUrl}/profile/document/shuftipro/countries`;
    return (this.netVerifiedCountries$ = this.#httpClient.get(url).pipe(
      map((res: []) =>
        res
          .map((item) => NetVerifiedCountriesModel.fromJson(item))
          .sort((a, b) => {
            return a.countryName < b.countryName
              ? -1
              : a.countryName > b.countryName
                ? 1
                : 0;
          })
      )
    ));
  }

  getDocuments(accountId: number): Observable<Array<DocumentModel>> {
    const url = `${this.#baseUrl}/documents/?account_id=${accountId}`;
    return this.#httpClient
      .get(url)
      .pipe(map((json: any[]) => json.map((d) => DocumentModel.fromJson(d))));
  }

  getStyleList(): Observable<ProfileStyleModel[]> {
    const params = new HttpParams().set('v', 'snake');
    const url = `${this.#baseUrl}/styles`;
    return this.#httpClient
      .get(url, { params })
      .pipe(
        map((json: Array<any>) =>
          json.map((style) => ProfileStyleModel.fromJson(style))
        )
      );
  }

  getAllPhotos(photo_ids: string[], accountId: number) {
    const url = `${this.#baseUrl}/photos/_bulk`;

    const body = {
      account_id: accountId,
      photo_ids: photo_ids
    };

    return this.#httpClient
      .post(url, body)
      .pipe(
        map((json: any[]) =>
          json.map((item) => ProfilePhotoModel.fromJson(item))
        )
      );
  }

  createPerson(name: string, account_id: number): Observable<{ id: string }> {
    const url = `${this.#baseUrl}/persons`;
    const body = { name, account_id };

    return this.#httpClient.post<{ id: string }>(url, body);
  }

  copyProfile(
    profileId: string,
    options: CopyProfileOptions
  ): Observable<{ profile_id: string }> {
    const url = `${this.#baseUrl}/${profileId}/_copy`;

    const body = {
      copy_photos: options.copyPhotos,
      person_id: options.personId
    };

    return this.#httpClient.post<{ profile_id: string }>(url, body);
  }

  getPersonById(personId: string): Observable<PersonModel> {
    const url = `${this.#baseUrl}/person/${personId}`;

    return this.#httpClient
      .get(url)
      .pipe(map((json) => PersonModel.fromJson(json)));
  }

  createProfile(
    data: ICreateProfileReqBody,
    account_id: number
  ): Observable<{ id: string }> {
    const url = `${this.#baseUrl}`;
    const { personId, isMulti, isFast, isSecret } = data;

    const body = {
      account_id,
      person_id: isMulti ? null : personId,
      is_multi: isMulti,
      is_fast: isFast,
      is_secret: isSecret
    };

    return this.#httpClient.post<{ id: string }>(url, body);
  }

  changeProfileVisibility(
    profileId: string,
    isSecret: boolean
  ): Observable<{ modified: 0 | 1 }> {
    const url = `${this.#baseUrl}/${profileId}/visibility`;

    const body = {
      is_secret: isSecret
    };

    return this.#httpClient.patch<{ modified: 0 | 1 }>(url, body);
  }

  getAllProfileRev(
    profileIds: string[],
    account_id: number
  ): Observable<ProfileModel[]> {
    const url = `${this.#baseUrl}/revs/latest/_bulk`;

    const body = {
      account_id,
      profile_ids: profileIds
    };
    return this.#httpClient
      .post<ProfileModel[]>(url, body)
      .pipe(
        map((json: ProfileModel[]) =>
          json.map((item) => ProfileModel.fromJson(item))
        )
      );
  }

  createPhotos(
    imageIds: string[],
    accountId: number,
    personId: string
  ): Observable<ProfilePhotoModel[]> {
    const url = `${this.#baseUrl}/photos`;
    const body = {
      image_ids: imageIds,
      account_id: accountId,
      person_id: personId
    };
    if (!personId) delete body.person_id;

    return this.#httpClient
      .post<{ photo_id: string; image_id: string; number: number }[]>(url, body)
      .pipe(
        map((res) =>
          res.map((item) => ({
            id: item.photo_id,
            imageId: item.image_id,
            status: ProfilePhotoEnum.Pending,
            number: item.number,
            personId: personId,
            dateCreated: new Date()
          }))
        )
      );
  }

  deleteProfilePhoto(photoId: string): Observable<unknown> {
    const url = `${this.#baseUrl}/photos/delete-storage/${photoId}`;
    return this.#httpClient.delete(url);
  }

  deleteProfile(profileId: string): Observable<unknown> {
    const url = `${this.#baseUrl}/${profileId}`;

    return this.#httpClient.delete(url);
  }

  putProfileRevision(
    rev: Partial<IProfileToJson>,
    authUserId: number
  ): Observable<ProfileModel> {
    const url = `${this.#baseUrl}/rev`;
    const body = rev;
    body.account_id = authUserId;
    return this.#httpClient.put(url, body).pipe(
      map((revision) => {
        return ProfileModel.fromJson({
          ...rev,
          ...revision
        });
      })
    );
  }

  createRevision(
    revision: ProfileModel,
    accountId: number
  ): Observable<ProfileModel> {
    const url = `${this.#baseUrl}/rev`;
    const json = {
      ...ProfileModel.toJson(revision)
    };

    return this.#httpClient.put<ProfileModel>(url, json).pipe(
      map(({ id }) => ({
        ...revision,
        id,
        revisionMeta: {
          status: ProfileRevisionStatusEnum.Pending,
          number: 1,
          createdBy: accountId,
          processedBy: null,
          modifiedBy: null,
          isModifiable: true,
          isApprovable: false,
          isIncomplete: false,
          isDeleted: false,
          showName: '',
          dateCreated: new Date(),
          isVisiting: revision.isVisiting,
          profileId: revision.profileId,
          dateModified: null
        }
      }))
    );
  }

  createDocuments(
    data: ICreateDocumentReqBody[],
    accountId: number
  ): Observable<DocumentModel[]> {
    const url = `${this.#baseUrl}/documents/_bulk`;
    const body: any = { docs: data };
    return this.#httpClient.post<ICreateDocumentResBody[]>(url, body).pipe(
      map((relations) =>
        relations.map(
          ({ id, person_id, type, documents }) =>
            ({
              id: id,
              accountId,
              createdBy: accountId,
              dateCreated: new Date(),
              documents: documents,
              isDeleted: false,
              isAcceptable: true,
              personId: person_id,
              status: DocumentStatusEnum.Pending,
              type: type
            }) as DocumentModel
        )
      ),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  createDocument(data: ICreateSingleDocumentReqBody): Observable<unknown> {
    const url = `${this.#baseUrl}/documents`;
    const body = {
      documents: [{ id: data.fileId }],
      instructions: data.instructions?.trim()
        ? data.instructions?.trim()
        : null,
      person_id: data.personId,
      type: data.type
    };

    return this.#httpClient.post<{ id: string }>(url, body);
  }

  checkPendingDocument(personId: string): Observable<boolean> {
    const url = `${this.#baseUrl}/documents/check-pending/${personId}`;
    return this.#httpClient.get<boolean>(url);
  }

  getNotificationList(
    accountId: number,
    offset: number,
    limit: number,
    type: NotificationType
  ): Observable<IDocumentNotificationResponse> {
    const params = new HttpParams()
      .set('account_id', accountId.toString())
      .set('offset', offset.toString())
      .set('limit', limit.toString())
      .set('type', type);

    const url = `${this.#baseUrl}/notifications/list`;
    return this.#httpClient
      .get<IDocumentNotificationResponse>(url, { params })
      .pipe(
        map((data) => {
          data.notifications = data.notifications.map((json) =>
            NotificationModel.fromJSON(json)
          );
          return data;
        })
      );
  }

  getNotificationsCount(
    accountId: number,
    type: NotificationType
  ): Observable<INotificationCountResponse> {
    const params = new HttpParams()
      .set('account_id', accountId.toString())
      .set('type', type);

    const url = `${this.#baseUrl}/notifications/count`;
    return this.#httpClient.get<INotificationCountResponse>(url, {
      params
    });
  }

  createIdenfyAccessToken(
    personId: string
  ): Observable<{ url: string; status: IdenfyStatusEnum }> {
    const url = `${environment.apiUrl}/escort-profile/document/idenfy/create-access-token`;

    return this.#httpClient.post<{ url: string; status: IdenfyStatusEnum }>(
      url,
      { person_id: personId }
    );
  }

  updateDocumentNotification(
    notificationId: string,
    body: { is_seen: boolean }
  ): Observable<boolean> {
    const url = `${this.#baseUrl}/notifications/${notificationId}`;
    return this.#httpClient.patch<boolean>(url, body);
  }

  createDocumentManual(body: {
    person_id: string;
    documents: { id: string }[];
    type: DocumentTypeEnum;
    id_type: ManualUploadedDocumentEnum;
    instructions: null;
  }): Observable<{ id: string }> {
    const url = `${this.#baseUrl}/documents`;

    return this.#httpClient.post<{ id: string }>(url, body);
  }

  createEvent(
    reqBody: IEventReqBody[],
    profileId: string
  ): Observable<string[]> {
    const url = `${this.#baseUrl}/events/${profileId}`;

    return this.#httpClient.post<string[]>(url, reqBody).pipe(
      map((res) => {
        if (!Array.isArray(res)) {
          return Object.values(res);
        }

        return res;
      })
    );
  }

  getEvents(
    event_ids: Pick<ProfileEventModel, 'id'>[]
  ): Observable<ProfileEventModel[]> {
    const url = `${this.#baseUrl}/events/bulk`;

    return this.#httpClient
      .post<
        ProfileEventModel[]
      >(url, { event_ids: event_ids.map((item) => item.id) })
      .pipe(
        map((data) => data.map((item) => ProfileEventModel.fromJson(item)))
      );
  }

  sendGoldVerifiedRequest(personId: string): Observable<{ created: 0 | 1 }> {
    const url = `${this.#baseUrl}/person/${personId}/gold-verification/request`;

    return this.#httpClient.post<{ created: 0 | 1 }>(url, {});
  }
}
